import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';

interface IgnoreSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Confirms the ignore with the (optional, trimmed) reason. The reason is
   * persisted on the AGENT hidden review and shown on hover across the
   * compliance surfaces. Empty string when the agent gave no reason.
   */
  onConfirm: (message: string) => void | Promise<void>;
  /** True while the ignore mutation is in flight. */
  isLoading?: boolean;
}

/**
 * Reason prompt shown when the agent ignores a document suggestion on the agent
 * compliance review page. Mirrors the "Hide validation" reason box on the
 * compliance sidebar (the reason is optional) while matching the agent page's
 * dark "Resubmit to reviewer" modal styling for visual consistency.
 */
export const IgnoreSuggestionModal: FC<IgnoreSuggestionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [message, setMessage] = useState('');

  // Start each open from a clean reason box (discard any abandoned edits).
  useEffect(() => {
    if (isOpen) {
      setMessage('');
    }
  }, [isOpen]);

  const handleConfirm = async (): Promise<void> => {
    // Only close after the mutation resolves so a failure keeps the modal open
    // with the typed reason intact. The rejection is caught rather than left to
    // escape this click handler unhandled — the caller has already reported it,
    // and staying open is the whole signal the agent needs to retry.
    try {
      await onConfirm(message.trim());
    } catch {
      return;
    }
    onClose();
  };

  return (
    <Modal isCentered isOpen={isOpen} size="md" onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg="compliance.background.primary"
        borderColor="compliance.border.primary"
        borderWidth={1}
      >
        <ModalHeader
          color="compliance.text.primary"
          fontSize="lg"
          fontWeight={600}
        >
          Ignore suggestion
        </ModalHeader>
        <ModalCloseButton color="compliance.text.secondary" />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Text color="compliance.text.secondary" fontSize="sm">
              Tell the reviewer why this isn't relevant (optional). They'll see
              it next to the suggestion, and it'll be included when you
              resubmit.
            </Text>
            <Textarea
              autoFocus
              _focus={{ borderColor: 'compliance.input.focus.border' }}
              _placeholder={{ color: 'compliance.input.placeholder' }}
              aria-label="Reason for ignoring"
              bg="compliance.input.bg"
              borderColor="compliance.input.border"
              color="compliance.input.text"
              fontSize="sm"
              minH="80px"
              placeholder="Why is this suggestion not relevant? (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button
            _hover={{ bg: 'compliance.button.secondary.hover' }}
            bg="compliance.button.secondary.bg"
            color="compliance.button.secondary.text"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            _hover={{ bg: 'compliance.button.primary.hover' }}
            bg="compliance.button.primary.bg"
            color="compliance.button.primary.text"
            isLoading={isLoading}
            onClick={() => {
              void handleConfirm();
            }}
          >
            Ignore
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
